import { ReactElement, useMemo } from "react";
import { getLinkedDocumentId } from "./useAddDocuments";
import { DocumentStoreType } from "client/Api/Documents/common";
import { localize } from "client_legacy/ndaw/culture";
import { GridItem, Icon } from "client/Components";
import { ActionAndOthers } from "client_legacy/ndaw/actions";
import { FileThumbnailKind, getDocumentKind } from "client_legacy/Documents/fileExtensions";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import { generateTrackingId } from "client/Misc/utils";
import { LinkedDocumentFormData } from "./common";

export type LinkedDocumentItemProps<TActionContext> = {
	model: LinkedDocumentFormData;
	isWebDavAvailable: boolean;
	isSharepointEnabled: boolean;
	isTouch: boolean;
	hasError: boolean;
	actions: readonly ActionAndOthers<TActionContext>[];
	actionContext: TActionContext;
	onClick(): void;
};

export const LinkedDocumentItem = <TActionContext, >(props: LinkedDocumentItemProps<TActionContext>): ReactElement => {
	const {
		model,
		isWebDavAvailable,
		isSharepointEnabled,
		isTouch,
		hasError,
		actions,
		actionContext,
		onClick
	} = props;

	const {
		name,
		extension,
		documentStoreType
	} = model;

	const hasWebDavIssue: boolean = documentStoreType === DocumentStoreType.WebDav
		&& !isWebDavAvailable;

	const hasSharePointIssue: boolean = documentStoreType === DocumentStoreType.SharePoint
		&& !isSharepointEnabled;

	const isDisabled: boolean = hasWebDavIssue || hasSharePointIssue;

	const tooltip = useMemo(
		(): string | undefined => {
			if (hasSharePointIssue) {
				return localize("_CantOpenBecauseSharePointIsNotConfigured");
			}

			if (hasWebDavIssue) {
				return localize("_CantOpenBecauseWebDavIsNotConfigured");
			}

			return;
		},
		[hasSharePointIssue, hasWebDavIssue]
	);

	const id = useMemo(
		(): string => getLinkedDocumentId(model),
		[model]
	);

	const type: FileThumbnailKind = getDocumentKind(
		DocumentItemType.File,
		extension
	);

	return (
		<GridItem
			actions={actions}
			actionContext={actionContext}
			actionsTitle={name}
			hasError={hasError}
			isTouch={isTouch}
			onClick={onClick}
			trackingId={generateTrackingId("linked-document-item", `|${id}`)}
			isDisabled={isDisabled}
			tooltip={tooltip}
		>
			<GridItem.Content
				actions={actions}
				actionContext={actionContext}
				name={name}
				thumbnail={
					<Icon
						value={{ kind: "svg", name: type }}
					/>
				}
			/>
		</GridItem>
	);
};
LinkedDocumentItem.displayName = "LinkedDocumentItem";